import _ from "lodash";
import { useMemo } from "react";

import {
  Collection,
  CollectionAsset,
  CollectionChangeRequest,
  CollectionConversationChangeRequest,
  CollectionSectionChangeRequest,
} from "../types/collections";
import { User, UserDetails } from "src/types/users";

const DefaultThumbnail = '/images/default_user_profile_background.png';

export function persistSortPreference(sortBy: string, orderBy: string) {
  const sort = {
    sortBy: sortBy,
    orderBy: orderBy,
  };

  window.localStorage.setItem("collectionSort", JSON.stringify(sort));
}

export function getSortPreference() {
  const sort = window.localStorage.getItem("collectionSort");
  return sort ? JSON.parse(sort) : { sortBy: "name", orderBy: "asc" };
}

export const calculateUpdateRequest = (source, target) => {
  const changes: CollectionChangeRequest = {};

  if (!_.isEqual(source.subject, target.subject) && target.subject !== "") {
    changes.name = target.subject;
  }

  if (!_.isEqual(source.template, target.template)) {
    changes.template = target.template;
  }

  if (!_.isEqual(source.banner, target.banner)) {
    changes.banner = { ...target.banner };
    const sourceBackground = _.get(source, "banner.background.id");
    const targetBackground = _.get(target, "banner.background.id");

    if (sourceBackground !== targetBackground) {
      _.set(changes, "banner.background", targetBackground);
    } else {
      _.set(changes, "banner.background", null);
    }
  }

  if (!_.isEqual(source.customerLogo, target.customerLogo)) {
    changes.customerLogo = _.get(target, "customerLogo.id") || null;
  }

  if (!_.isEqual(source.viewerType, target.viewerType)) {
    changes.viewerType = target.viewerType;
  }

  if (!_.isEqual(source.assetViewer, target.assetViewer)) {
    changes.assetViewer = target.assetViewer;
  }

  const sourceAssets = prepareAssetUpdate(source.entities || []);
  const targetAssets = prepareAssetUpdate(target.entities || []);

  if (!_.isEqual(sourceAssets, targetAssets)) {
    changes.entities = targetAssets;
  }

  if (target.sections && target.sections.length > 0) {
    const sourceSections = source.sections || [];

    changes.sections = target.sections
      .map((section) => {
        //Find the section in source sections
        const sourceSection = sourceSections.find((item) => {
          return item.item?.id == section.item?.id;
        });

        return calculateSectionChanged(sourceSection, section);
      })
      .filter((change) => change != null);
  }

  if (source.sections && source.sections.length > 0) {
    const targetSections = target.sections || [];

    changes.deletedSections = source.sections
      .filter((section) => {
        const found = targetSections.find((item) => {
          return section.item?.id == item.item?.id;
        });

        return found == null;
      })
      .map((item) => item.item.id)
      .filter((item) => item != null);
  }

  return changes;
};

export const calculateUpdateConversationRequest = (source, target) => {
  const changes: CollectionConversationChangeRequest = {};

  if (!_.isEqual(source.subject, target.subject) && target.subject !== "") {
    changes.name = target.subject;
  }

  if (!_.isEqual(source.template, target.template)) {
    changes.template = target.template;
  }

  if (!_.isEqual(source.banner, target.banner)) {
    changes.banner = { ...target.banner };
    const sourceBackground = _.get(source, "banner.background.id");
    const targetBackground = _.get(target, "banner.background.id");

    if (sourceBackground !== targetBackground) {
      _.set(changes, "banner.background", targetBackground);
    } else {
      _.set(changes, "banner.background", null);
    }
  }

  if (!_.isEqual(source.customerLogo, target.customerLogo)) {
    changes.customerLogo = _.get(target, "customerLogo.id") || null;
  }

  if (!_.isEqual(source.viewerType, target.viewerType)) {
    changes.viewerType = target.viewerType;
  }

  if (!_.isEqual(source.assetViewer, target.assetViewer)) {
    changes.assetViewer = target.assetViewer;
  }

  const sourceAssets = prepareAssetUpdate(source.entities || []);
  const targetAssets = prepareAssetUpdate(target.entities || []);

  if (!_.isEqual(sourceAssets, targetAssets)) {
    changes.assets = targetAssets;
  }

  if (target.sections && target.sections.length > 0) {
    const sourceSections = source.sections || [];

    changes.sections = target.sections
      .map((section) => {
        //Find the section in source sections
        const sourceSection = sourceSections.find((item) => {
          return item.item?.id == section.item?.id;
        });

        return calculateSectionChanged(sourceSection, section);
      })
      .filter((change) => change != null);
  }

  if (source.sections && source.sections.length > 0) {
    const targetSections = target.sections || [];

    changes.deletedSections = source.sections
      .filter((section) => {
        const found = targetSections.find((item) => {
          return section.item?.id == item.item?.id;
        });

        return found == null;
      })
      .map((item) => item.item.id)
      .filter((item) => item != null);
  }

  return changes;
};

export const calculateSectionChanged = (source, target) => {
  const targetAssets = prepareAssetUpdate(target.item?.entities || []);
  if (!source) {
    return {
      id: target.item?.id,
      name: target.item?.name,
      entities: targetAssets,
    };
  }

  const sourceAssets = prepareAssetUpdate(source.item?.entities || []);

  const changes: CollectionSectionChangeRequest = {
    id: target.item?.id,
  };

  if (!_.isEqual(source.item?.name, target.item?.name)) {
    if (target.item?.name && target.item?.name.trim() !== "") {
      changes.name = target.item?.name;
    }
  }

  if (!_.isEqual(sourceAssets, targetAssets)) {
    changes.entities = targetAssets;
  }

  return changes;
};

const prepareAssetUpdate = (assets) => {
  if (!assets) {
    return [];
  }

  return assets.map((asset, index) => {
    return {
      id: asset.id,
      order: index + 1,
      entityMetadata: asset.metadata,
      type: asset.type || "ASSET",
    };
  });
};

//Calculate if notification email should be sent on collection change
export const shouldNotifyOnChange = (source: Collection, target: Collection) => {
  const sourceCollection = {
    id: _.get(source, "id"),
    name: _.get(source, "name"),
    assets: getAssetsInCollection(source),
    description: _.get(source, "template"),
  };

  const targetCollection = {
    id: _.get(target, "id"),
    name: _.get(target, "name"),
    assets: getAssetsInCollection(target),
    description: _.get(target, "template"),
  };

  return !_.isEqual(sourceCollection, targetCollection);
};

const getAssetsInCollection = (collection) => {
  const assetIds: string[] = [];

  const sections = _.get(collection, "sections");

  if (sections && sections.length > 0) {
    sections.forEach((section) => {
      if (section.loaded && section.item.entities) {
        section.item.entities.forEach((asset) => {
          if (_.get(asset, "asset.entityType") === "asset") {
            assetIds.push(asset.id);
          }
        });
      }
    });
  } else {
    const assets = _.get(collection, "entities");
    if (assets && assets.length > 0) {
      assets.forEach((asset) => {
        if (_.get(asset, "asset.entityType") === "asset") {
          assetIds.push(asset.id);
        }
      });
    }
  }

  return assetIds.sort();
};

export const generateDefaultContactCard = () => {
  const uuid = crypto.randomUUID();
  const contactCard = {
    id: uuid,
    asset: {
      id: uuid,
    },
    type: "CONTACT_CARD",
    order: -1,
    metadata: {
      width: 1,
      height: 1,
      xaxis: 3,
      yaxis: 0,
      contactCardLayout: "split",
    },
  } as any as CollectionAsset;
  
  return contactCard;
};

export const getContactCardExists = (entityList: CollectionAsset[]) => {
  return !!_.find(entityList, (collectionAsset: CollectionAsset) => {
    return _.get(collectionAsset, "type") === "CONTACT_CARD";
  });
}

export const useUserDetails = (user: User): UserDetails => {
  const userDetails = useMemo(() => {
    const userName = `${_.get(user, "firstName")}${_.get(user, "lastName", "").length > 0 ? " " + _.get(user, "lastName", "") : ""}` || "";
    const userTitle = _.get(user, "title", "") || "";
    const userEmail = _.get(user, "email", "") || "";
    const userImage = _.get(user, "icon.full") || DefaultThumbnail;
    const userContactNumber = _.get(user, "mobilePhone") || _.get(user, "workPhone") || "";
    const linkedInUrl = _.get(user, "linkedinUrl", "") || "";
    
    return {
      userName,
      userTitle,
      userEmail,
      userImage,
      userContactNumber,
      linkedInUrl,
    }
  }, [user]);
  
  return userDetails;
}